// src/components/tickets/detail/KbSourcesCard.tsx
//
// KB provenance (Story 4.2 AC 5). Pins render as links (document title, version
// number, similarity in meta typography, + a "retired" marker when the
// document's status is retired) opening /kb/:documentId/versions/:versionId —
// THE PINNED VERSION, even if the document has newer versions or was retired
// (2.2's deep-link contract; the screen NEVER re-retrieves or links the latest
// version). A ticket with zero pins shows the quiet FR-40 provenance fact —
// never a demotion or warning (3.6 Decision 15).
//
// KbSourceLinks is exported so the DraftPanel can repeat the same links inside
// its canvas-inset without forking the rendering.

import { Link } from 'react-router-dom'
import type { YakKbPinWithDocument } from '../../../types/yak'

const ZERO_PIN_COPY = 'No knowledge-base sources were logged for this answer.'

export function KbSourceLinks({ pins }: { pins: YakKbPinWithDocument[] }) {
  if (pins.length === 0) {
    return <p className="text-sm text-gray-500">{ZERO_PIN_COPY}</p>
  }
  return (
    <ul className="space-y-2">
      {pins.map(pin => (
        <li key={pin.id} className="text-sm">
          <Link
            to={`/kb/${pin.document_id}/versions/${pin.document_version_id}`}
            className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
          >
            {pin.title}
          </Link>
          <div className="text-xs text-gray-500">
            <span>Version {pin.version_number}</span>
            {typeof pin.similarity === 'number' ? (
              <span> · {(pin.similarity * 100).toFixed(0)}% match</span>
            ) : null}
            {pin.status === 'retired' ? (
              <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                retired
              </span>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  )
}

export function KbSourcesCard({ pins }: { pins: YakKbPinWithDocument[] }) {
  return (
    <section className="card p-5" aria-labelledby="kb-sources-heading">
      <h2
        id="kb-sources-heading"
        className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500"
      >
        Knowledge-base sources
      </h2>
      <KbSourceLinks pins={pins} />
    </section>
  )
}
